import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageWrapper from '@/components/PageWrapper';
import ImageCarousel from '@/components/ImageCarousel';
import { FadeUp, SlideLeft, SlideRight, StaggerContainer, StaggerItem } from '@/components/ScrollReveal';

const AboutPage = () => {
  const carouselImages = [
    { src: '/images/REFORMER HERO.jpg', alt: 'Reformer', caption: 'Reformer Work' },
    { src: '/images/CHAIR HERO 2.jpg', alt: 'Chair', caption: 'Chair Work' },
    { src: '/images/LADDER BARREL TREE HERO.jpg', alt: 'Ladder Barrel', caption: 'Ladder Barrel' },
    { src: '/images/CAD.jpg', alt: 'Cadillac', caption: 'Cadillac' },
    { src: '/images/ELEPHANT CHAIR.jpg', alt: 'Elephant Chair', caption: 'Elephant on Chair' },
  ];

  const methodPoints = ['Strength', 'Mobility', 'Breath', 'Balance', 'Coordination'];

  const sessionFocus = [
    'Improving movement technique',
    'Supporting posture and spinal health',
    'Building strength with control',
    'Enhancing mobility and coordination',
    'Developing body awareness and confidence',
  ];

  return (
    <PageWrapper>
      {/* Hero Section */}
      <section data-testid="about-hero" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">
              About TEZR
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-stone-900 leading-[1.1] tracking-tight">
              Empowering You<br />
              <span className="italic">To Thrive</span>
            </h1>
          </FadeUp>
        </div>
      </section>

      {/* The TEZR Idea */}
      <section className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <SlideLeft>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">
                The Concept
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl text-stone-900 leading-[1.1] tracking-tight">
                The TEZR <span className="italic">Idea</span>
              </h2>
              <p className="mt-8 text-stone-600 text-lg leading-relaxed font-light">
                TEZR is a play on the word Teaser — the iconic Pilates exercise that challenges 
                balance, strength, coordination, concentration, and mobility all in one movement. 
                That's the essence of TEZR Pilates. Everything working together. Nothing isolated.
              </p>
            </SlideLeft>

            <SlideRight>
              <div className="space-y-8">
                <div className="p-8 bg-white border border-stone-200">
                  <h3 className="font-heading text-2xl text-stone-900 mb-4">Who It's For</h3>
                  <p className="text-stone-600 leading-relaxed font-light">
                    Suited to both beginners and advanced clients. If you value precision, 
                    private attention, and long-term progress — you're in the right place.
                  </p>
                </div>
                <div className="p-8 bg-white border border-stone-200">
                  <h3 className="font-heading text-2xl text-stone-900 mb-4">Beginner to Advanced</h3>
                  <p className="text-stone-600 leading-relaxed font-light">
                    New to Pilates? We build strong foundations from the start. 
                    Experienced? We refine, progress, and challenge your practice with precision.
                  </p>
                </div>
              </div>
            </SlideRight>
          </div>
        </div>
      </section>

      {/* Image Carousel */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <ImageCarousel images={carouselImages} />
          </FadeUp>
        </div>
      </section>

      {/* The Method */}
      <section data-testid="method-section" className="py-24 md:py-32 bg-stone-900 text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4 text-center">
              Our Approach
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl text-white leading-[1.1] tracking-tight text-center mb-16">
              The <span className="italic">Method</span>
            </h2>
          </FadeUp>

          <StaggerContainer className="grid md:grid-cols-2 gap-8">
            <StaggerItem>
              <div className="p-8 border border-stone-700">
                <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4">
                  Technique First
                </h3>
                <p className="text-stone-300 leading-relaxed font-light">
                  TEZR Pilates is built on the belief that good movement starts with good technique. 
                  This is not about rushing through exercises. It's about understanding them, 
                  refining them, and allowing strength and confidence to follow naturally.
                </p>
              </div>
            </StaggerItem>

            <StaggerItem>
              <div className="p-8 border border-stone-700">
                <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4">
                  Integrated Movement
                </h3>
                <p className="text-stone-300 leading-relaxed font-light mb-4">
                  Pilates is not about individual muscles — it's about how the whole system works together.
                </p>
                <div className="flex flex-wrap gap-2">
                  {methodPoints.map((point) => (
                    <span
                      key={point}
                      className="px-3 py-1 text-xs uppercase tracking-wider text-stone-400 border border-stone-600"
                    >
                      {point}
                    </span>
                  ))}
                </div>
              </div>
            </StaggerItem>

            <StaggerItem>
              <div className="p-8 border border-stone-700">
                <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4">
                  Private One-on-One
                </h3>
                <p className="text-stone-300 leading-relaxed font-light mb-4">
                  Each session is fully customised. Sessions focus on:
                </p>
                <ul className="space-y-2">
                  {sessionFocus.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-stone-400 text-sm font-light">
                      <CheckCircle className="flex-shrink-0 mt-0.5" size={14} strokeWidth={1.5} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </StaggerItem>

            <StaggerItem>
              <div className="p-8 border border-stone-700">
                <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4">
                  Classical. Contemporary. Complete.
                </h3>
                <p className="text-stone-300 leading-relaxed font-light">
                  Classical Pilates provides structure, sequence, and integrity. 
                  Contemporary techniques allow intelligent adaptation for modern bodies — 
                  desk posture, sport demands, injuries, and daily life. The result is a 
                  complete system that supports how you actually move today.
                </p>
              </div>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* Otto Prodan */}
      <section data-testid="otto-section" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <SlideLeft>
              <div className="image-zoom aspect-[3/4]">
                <img
                  src="/images/otto.jpeg"
                  alt="Otto Prodan"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
            </SlideLeft>

            <SlideRight>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">
                Meet Your Trainer
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl text-stone-900 leading-[1.1] tracking-tight">
                Otto <span className="italic">Prodan</span>
              </h2>
              <p className="mt-2 text-stone-500 font-light">
                TEZR Owner and Pilates Trainer
              </p>
              <p className="mt-8 text-stone-600 text-lg leading-relaxed font-light">
                I have been teaching fitness for 14 years, with a strong foundation in group 
                fitness and certified personal training. Over the last 4 years I've specialised 
                in yoga and Pilates, where my passion truly lies.
              </p>
              <p className="mt-6 text-stone-600 text-lg leading-relaxed font-light">
                I'm a STOTT Pilates® Comprehensive trained teacher with over 500 hours of study. 
                STOTT Pilates is widely recognised in the fitness industry for its thorough, 
                science-backed training and high education standards.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <span className="px-4 py-2 text-xs uppercase tracking-wider text-stone-600 border border-stone-300">
                  14 Years Experience
                </span>
                <span className="px-4 py-2 text-xs uppercase tracking-wider text-stone-600 border border-stone-300">
                  STOTT PILATES®
                </span>
                <span className="px-4 py-2 text-xs uppercase tracking-wider text-stone-600 border border-stone-300">
                  500+ Hours
                </span>
              </div>
              <Button
                asChild
                className="mt-8 btn-primary"
              >
                <Link to="/contact">
                  Get in Touch
                  <ArrowRight className="ml-2" size={16} strokeWidth={1.5} />
                </Link>
              </Button>
            </SlideRight>
          </div>
        </div>
      </section>

      {/* Bottom Images */}
      <section className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <div className="grid grid-cols-3 gap-4">
              <div className="image-zoom aspect-[4/5]">
                <img
                  src="/images/CHAIR SIDE LIFT.jpg"
                  alt="Chair Side Lift"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
              <div className="image-zoom aspect-[4/5]">
                <img
                  src="/images/REFORMER DIAGONAL REach.jpg"
                  alt="Reformer Diagonal Reach"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
              <div className="image-zoom aspect-[4/5]">
                <img
                  src="/images/HERO CADI CLOSER SHOT.jpg"
                  alt="Cadillac"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
};

export default AboutPage;
